import Template from "../../components/Template";
import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import AlertDialog from "../../components/AlertDialog";
import * as XLSX from "xlsx-js-style";
import { formatDateWithoutHour } from "../../utils/date";
import { formatCurrencyFromDb } from "../../utils/masks";

const TOTAIS_ZERADOS = {
    materiais: 0,
    valorTotal: 0,
    economiaPotencial: 0,
    materiaisComparaveis: 0,
    materiaisSemComparacao: 0,
    fornecedoresDistintos: 0,
};

const FILTROS_INICIAIS = {
    obraId: "",
    dataInicio: "",
    dataFim: "",
    chaveComparacao: "descricao",
    escopoComparacao: "global",
    busca: "",
};

const formatarQuantidade = (valor) =>
    (Number(valor) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Bloco de Pedidos de Compra (ESS-DAFMC-2026-0007): totais próprios, nunca somados
// aos das notas — o pedido pode já estar faturado numa NF sem vínculo com ela.
const PEDIDOS_VAZIO = { permitido: false, itens: [], totais: null };

// Preço unitário de pedido aceita até 4 casas (ex.: R$ 7,654); com 2 casas a
// conta quantidade x preço da linha não fecharia com o valor do item.
const formatarPrecoUnitario = (valor) =>
    valor === null || valor === undefined
        ? ""
        : Number(valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 4 });

const formatarQuantidadePedido = (valor) =>
    (Number(valor) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 4 });

const descreverNotaReferencia = (nota) => {
    if (!nota) return "";
    const data = nota.dataEmissao ? ` de ${formatDateWithoutHour(nota.dataEmissao)}` : "";
    return nota.tipo === "VINCULADA"
        ? `NF ${nota.numeroNota}${data} (vinculada ao pedido)`
        : `Possivelmente já faturado na NF ${nota.numeroNota}${data}`;
};

const RelatorioMateriaisObra = () => {
    const [obras, setObras] = useState([]);
    const [carregandoObras, setCarregandoObras] = useState(true);
    const [filters, setFilters] = useState(FILTROS_INICIAIS);
    // Guarda os filtros que geraram o resultado em tela: os avisos e o cabeçalho da
    // exportação precisam refletir o que foi consultado, não o que está digitado.
    const [filtrosAplicados, setFiltrosAplicados] = useState(null);
    const [records, setRecords] = useState([]);
    const [totais, setTotais] = useState(TOTAIS_ZERADOS);
    const [pedidosCompra, setPedidosCompra] = useState(PEDIDOS_VAZIO);
    const [obraSelecionada, setObraSelecionada] = useState(null);
    const [somenteComparaveis, setSomenteComparaveis] = useState(false);
    const [detalheAberto, setDetalheAberto] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [erroObra, setErroObra] = useState("");
    const [message, setMessage] = useState("");
    const [showAlertDialog, setShowAlertDialog] = useState(false);

    useEffect(() => {
        const carregarObras = async () => {
            try {
                const { data } = await api.get("/obras/listar/todas");
                setObras(data || []);
            } catch (error) {
                console.error("Erro ao carregar a lista de obras:", error);
                setMessage("Não foi possível carregar a lista de obras.");
                setShowAlertDialog(true);
            } finally {
                setCarregandoObras(false);
            }
        };
        carregarObras();
    }, []);

    const recordsExibidos = useMemo(
        () => (somenteComparaveis ? records.filter((r) => r.comparavel) : records),
        [records, somenteComparaveis]
    );

    const comparacaoPorNcm = filtrosAplicados?.chaveComparacao === "ncm";

    const fetchRelatorio = async () => {
        if (!filters.obraId) {
            setErroObra("Selecione a obra para gerar o relatório.");
            return;
        }
        if (filters.dataInicio && filters.dataFim && filters.dataFim < filters.dataInicio) {
            setMessage("A data final não pode ser anterior à data inicial.");
            setShowAlertDialog(true);
            return;
        }

        setErroObra("");
        setIsLoading(true);
        setHasSearched(true);
        try {
            const params = new URLSearchParams();
            params.append("obraId", filters.obraId);
            if (filters.dataInicio) params.append("dataInicio", filters.dataInicio);
            if (filters.dataFim) params.append("dataFim", filters.dataFim);
            params.append("chaveComparacao", filters.chaveComparacao);
            params.append("escopoComparacao", filters.escopoComparacao);
            if (filters.busca) params.append("busca", filters.busca);

            const response = await api.get(`/relatorios/materiais-obra?${params.toString()}`);
            setRecords(response.data.records || []);
            setTotais(response.data.totais || TOTAIS_ZERADOS);
            setPedidosCompra(response.data.pedidosCompra || PEDIDOS_VAZIO);
            setObraSelecionada(response.data.obra || null);
            setFiltrosAplicados({ ...filters });
            setDetalheAberto(null);
        } catch (error) {
            console.error("Erro ao buscar relatório de materiais por obra:", error);
            setMessage(error.response?.data?.error || "Erro ao buscar dados do relatório.");
            setShowAlertDialog(true);
            setRecords([]);
            setTotais(TOTAIS_ZERADOS);
            setPedidosCompra(PEDIDOS_VAZIO);
            setObraSelecionada(null);
            setFiltrosAplicados(null);
        } finally {
            setIsLoading(false);
        }
    };

    const clearFilters = () => {
        setFilters(FILTROS_INICIAIS);
        setFiltrosAplicados(null);
        setRecords([]);
        setTotais(TOTAIS_ZERADOS);
        setPedidosCompra(PEDIDOS_VAZIO);
        setObraSelecionada(null);
        setSomenteComparaveis(false);
        setDetalheAberto(null);
        setHasSearched(false);
        setErroObra("");
    };

    const periodoDescricao = () => {
        const inicio = filtrosAplicados?.dataInicio;
        const fim = filtrosAplicados?.dataFim;
        if (inicio && fim) return `${formatDateWithoutHour(inicio)} a ${formatDateWithoutHour(fim)}`;
        if (inicio) return `A partir de ${formatDateWithoutHour(inicio)}`;
        if (fim) return `Até ${formatDateWithoutHour(fim)}`;
        return "Todo o período";
    };

    const itensPedido = pedidosCompra.permitido ? pedidosCompra.itens || [] : [];

    const exportarExcel = () => {
        if (recordsExibidos.length === 0 && itensPedido.length === 0) {
            setMessage("Não há dados para exportar.");
            setShowAlertDialog(true);
            return;
        }

        const borderThin = { style: "thin", color: { rgb: "B0B0B0" } };
        const borders = { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin };

        const tituloStyle = {
            font: { bold: true, sz: 12, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "D9E7F5" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borders,
        };
        const subtituloStyle = {
            font: { bold: true, sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "DCE6F1" } },
            alignment: { horizontal: "left", vertical: "center" },
            border: borders,
        };
        const headerStyle = {
            font: { bold: true, sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "DCE6F1" } },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: borders,
        };
        const cellStyle = {
            font: { sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "EAF1F8" } },
            alignment: { vertical: "center", horizontal: "left" },
            border: borders,
        };
        const cellCurrencyStyle = {
            ...cellStyle,
            alignment: { vertical: "center", horizontal: "right" },
            numFmt: "R$ #,##0.00",
        };
        const cellNumberStyle = {
            ...cellStyle,
            alignment: { vertical: "center", horizontal: "right" },
            numFmt: "#,##0.00",
        };
        const totalStyle = {
            font: { bold: true, sz: 10, color: { rgb: "000000" } },
            fill: { fgColor: { rgb: "FCE4D6" } },
            alignment: { vertical: "center", horizontal: "right" },
            border: borders,
            numFmt: "R$ #,##0.00",
        };
        const totalLabelStyle = { ...totalStyle, numFmt: "General" };

        // Planilha das notas fiscais — conteúdo e totais iguais aos de antes do
        // bloco de pedidos; só passou a ser montada por função.
        const montarPlanilhaNotas = () => {
            const cabecalhos = [
                "MATERIAL",
                "NCM",
                "UN",
                "QTD NA OBRA",
                "VALOR NA OBRA",
                "PREÇO MÉDIO NA OBRA",
                "FORNECEDORES NA OBRA",
                "MENOR PREÇO",
                "FORNECEDOR (MENOR)",
                "CNPJ (MENOR)",
                "NF REF. (MENOR)",
                "MAIOR PREÇO",
                "FORNECEDOR (MAIOR)",
                "CNPJ (MAIOR)",
                "NF REF. (MAIOR)",
                "VARIAÇÃO %",
                "ECONOMIA POTENCIAL",
                "FORNECEDORES COMPARADOS",
            ];
            const colCount = cabecalhos.length;

            const wsData = [];
            const merges = [];

            const linhaTitulo = Array(colCount).fill("");
            linhaTitulo[0] = "RELATÓRIO DE MATERIAIS POR OBRA";
            wsData.push(linhaTitulo);
            merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });

            const linhaObra = Array(colCount).fill("");
            linhaObra[0] = `OBRA: ${obraSelecionada ? `${obraSelecionada.codigoProjeto} - ${obraSelecionada.nomeProjeto}`.toUpperCase() : ""}`;
            wsData.push(linhaObra);
            merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } });

            const linhaParams = Array(colCount).fill("");
            linhaParams[0] = `PERÍODO: ${periodoDescricao()} · COMPARAÇÃO: ${comparacaoPorNcm ? "MESMO NCM E UNIDADE (INDICATIVA)" : "MESMA DESCRIÇÃO DE CATÁLOGO"} · HISTÓRICO: ${filtrosAplicados?.escopoComparacao === "obra" ? "SOMENTE ESTA OBRA" : "TODAS AS OBRAS"}`;
            wsData.push(linhaParams);
            merges.push({ s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } });

            const headerRowIndex = wsData.length;
            wsData.push(cabecalhos);

            const dataStartRow = wsData.length;
            recordsExibidos.forEach((r) => {
                wsData.push([
                    (r.descricao || "").toUpperCase(),
                    r.ncm || "",
                    r.unidadeMedida || "",
                    Number(r.quantidadeTotal) || 0,
                    Number(r.valorTotal) || 0,
                    Number(r.precoMedioObra) || 0,
                    r.fornecedoresObra.map((f) => f.fornecedor).join(" · ").toUpperCase(),
                    r.comparavel ? Number(r.menorPreco?.valorUnitario) || 0 : "",
                    r.comparavel ? (r.menorPreco?.fornecedor || "").toUpperCase() : "SEM COMPARAÇÃO",
                    r.comparavel ? r.menorPreco?.cnpjFornecedor || "" : "",
                    r.comparavel && r.menorPreco?.numeroNota
                        ? `NF ${r.menorPreco.numeroNota}${r.menorPreco.dataEmissao ? ` (${formatDateWithoutHour(r.menorPreco.dataEmissao)})` : ""}`
                        : "",
                    r.comparavel ? Number(r.maiorPreco?.valorUnitario) || 0 : "",
                    r.comparavel ? (r.maiorPreco?.fornecedor || "").toUpperCase() : "",
                    r.comparavel ? r.maiorPreco?.cnpjFornecedor || "" : "",
                    r.comparavel && r.maiorPreco?.numeroNota
                        ? `NF ${r.maiorPreco.numeroNota}${r.maiorPreco.dataEmissao ? ` (${formatDateWithoutHour(r.maiorPreco.dataEmissao)})` : ""}`
                        : "",
                    r.comparavel && r.variacaoPercentual != null ? Number(r.variacaoPercentual) : "",
                    r.comparavel ? Number(r.economiaPotencial) || 0 : "",
                    r.fornecedoresComparados || 0,
                ]);
            });
            const dataEndRow = wsData.length - 1;

            const totalRowIndex = wsData.length;
            const linhaTotal = Array(colCount).fill("");
            linhaTotal[0] = "TOTAL";
            linhaTotal[4] = recordsExibidos.reduce((acc, r) => acc + (Number(r.valorTotal) || 0), 0);
            // Em comparação por NCM a economia é indicativa: some apenas na comparação
            // por descrição, para o número exportado não virar meta de gestão.
            linhaTotal[16] = comparacaoPorNcm
                ? ""
                : recordsExibidos.reduce((acc, r) => acc + (Number(r.economiaPotencial) || 0), 0);
            wsData.push(linhaTotal);
            merges.push({ s: { r: totalRowIndex, c: 0 }, e: { r: totalRowIndex, c: 3 } });

            const worksheet = XLSX.utils.aoa_to_sheet(wsData);
            worksheet["!merges"] = merges;

            const aplicarEstilo = (r, c, estilo, numerico = false) => {
                const ref = XLSX.utils.encode_cell({ r, c });
                if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
                worksheet[ref].s = estilo;
                if (numerico && worksheet[ref].v !== "") worksheet[ref].t = "n";
            };

            for (let c = 0; c < colCount; c++) aplicarEstilo(0, c, tituloStyle);
            for (let c = 0; c < colCount; c++) aplicarEstilo(1, c, subtituloStyle);
            for (let c = 0; c < colCount; c++) aplicarEstilo(2, c, subtituloStyle);
            for (let c = 0; c < colCount; c++) aplicarEstilo(headerRowIndex, c, headerStyle);

            const colunasMoeda = [4, 5, 7, 11, 16];
            const colunasNumero = [3, 15, 17];
            for (let r = dataStartRow; r <= dataEndRow; r++) {
                for (let c = 0; c < colCount; c++) {
                    if (colunasMoeda.includes(c)) aplicarEstilo(r, c, cellCurrencyStyle, true);
                    else if (colunasNumero.includes(c)) aplicarEstilo(r, c, cellNumberStyle, true);
                    else aplicarEstilo(r, c, cellStyle);
                }
            }

            for (let c = 0; c < colCount; c++) {
                const ehValor = colunasMoeda.includes(c);
                aplicarEstilo(totalRowIndex, c, ehValor ? totalStyle : totalLabelStyle, ehValor);
            }

            worksheet["!cols"] = [
                { wch: 48 }, { wch: 12 }, { wch: 6 }, { wch: 14 }, { wch: 16 }, { wch: 18 },
                { wch: 40 }, { wch: 14 }, { wch: 34 }, { wch: 20 }, { wch: 22 },
                { wch: 14 }, { wch: 34 }, { wch: 20 }, { wch: 22 },
                { wch: 12 }, { wch: 18 }, { wch: 14 },
            ];
            worksheet["!rows"] = [{ hpt: 26 }, { hpt: 20 }, { hpt: 20 }, { hpt: 32 }];
            return worksheet;
        };

        // Aba separada de propósito: os valores dos pedidos não entram no total das
        // notas (o pedido pode já estar faturado numa NF sem vínculo com ele).
        const montarPlanilhaPedidos = () => {
            const cabecalhos = [
                "OC",
                "EMISSÃO",
                "FORNECEDOR",
                "CNPJ",
                "STATUS",
                "ITEM",
                "DESCRIÇÃO",
                "QTD",
                "UN",
                "PREÇO UNITÁRIO",
                "VALOR",
                "NOTA DE REFERÊNCIA",
            ];
            const colCount = cabecalhos.length;
            const wsData = [];
            const merges = [];
            const linhaCheia = (texto) => {
                const linha = Array(colCount).fill("");
                linha[0] = texto;
                merges.push({ s: { r: wsData.length, c: 0 }, e: { r: wsData.length, c: colCount - 1 } });
                wsData.push(linha);
            };

            linhaCheia("ITENS DE PEDIDOS DE COMPRA DA OBRA");
            linhaCheia(`OBRA: ${obraSelecionada ? `${obraSelecionada.codigoProjeto} - ${obraSelecionada.nomeProjeto}`.toUpperCase() : ""} · PERÍODO (EMISSÃO DO PEDIDO): ${periodoDescricao().toUpperCase()}`);
            linhaCheia("PEDIDOS APROVADOS, EM ANDAMENTO, RECEBIDOS OU CONCLUÍDOS. ESTES VALORES NÃO ENTRAM NO TOTAL DAS NOTAS FISCAIS: UM PEDIDO PODE JÁ ESTAR FATURADO EM UMA NOTA. CONFIRA A COLUNA NOTA DE REFERÊNCIA.");

            const headerRowIndex = wsData.length;
            wsData.push(cabecalhos);
            const dataStartRow = wsData.length;
            itensPedido.forEach((i) => {
                wsData.push([
                    i.numeroOC || "",
                    i.dataEmissao ? formatDateWithoutHour(i.dataEmissao) : "",
                    (i.fornecedor || "").toUpperCase(),
                    i.cnpjFornecedor || "",
                    (i.status || "").toUpperCase(),
                    i.numeroItem ?? "",
                    (i.descricao || "").toUpperCase(),
                    Number(i.quantidade) || 0,
                    i.unidade || "",
                    Number(i.precoUnitario) || 0,
                    Number(i.valorTotal) || 0,
                    descreverNotaReferencia(i.notaReferencia).toUpperCase(),
                ]);
            });
            const dataEndRow = wsData.length - 1;

            const totalRowIndex = wsData.length;
            const linhaTotal = Array(colCount).fill("");
            linhaTotal[0] = `TOTAL — ${pedidosCompra.totais?.itens || 0} ITEM(NS) EM ${pedidosCompra.totais?.pedidos || 0} PEDIDO(S)`;
            linhaTotal[10] = Number(pedidosCompra.totais?.valorTotal) || 0;
            wsData.push(linhaTotal);
            merges.push({ s: { r: totalRowIndex, c: 0 }, e: { r: totalRowIndex, c: 9 } });

            const worksheet = XLSX.utils.aoa_to_sheet(wsData);
            worksheet["!merges"] = merges;
            const aplicarEstilo = (r, c, estilo, numerico = false) => {
                const ref = XLSX.utils.encode_cell({ r, c });
                if (!worksheet[ref]) worksheet[ref] = { v: "", t: "s" };
                worksheet[ref].s = estilo;
                if (numerico && worksheet[ref].v !== "") worksheet[ref].t = "n";
            };
            for (let c = 0; c < colCount; c++) {
                aplicarEstilo(0, c, tituloStyle);
                aplicarEstilo(1, c, subtituloStyle);
                aplicarEstilo(2, c, { ...subtituloStyle, alignment: { horizontal: "left", vertical: "center", wrapText: true } });
                aplicarEstilo(headerRowIndex, c, headerStyle);
            }
            const precoUnitarioStyle = { ...cellCurrencyStyle, numFmt: "R$ #,##0.00##" };
            const quantidadeStyle = { ...cellNumberStyle, numFmt: "#,##0.####" };
            for (let r = dataStartRow; r <= dataEndRow; r++) {
                for (let c = 0; c < colCount; c++) {
                    if (c === 10) aplicarEstilo(r, c, cellCurrencyStyle, true);
                    else if (c === 9) aplicarEstilo(r, c, precoUnitarioStyle, true);
                    else if (c === 7) aplicarEstilo(r, c, quantidadeStyle, true);
                    else aplicarEstilo(r, c, cellStyle);
                }
            }
            for (let c = 0; c < colCount; c++) {
                aplicarEstilo(totalRowIndex, c, c === 10 ? totalStyle : totalLabelStyle, c === 10);
            }
            worksheet["!cols"] = [
                { wch: 16 }, { wch: 11 }, { wch: 34 }, { wch: 20 }, { wch: 14 }, { wch: 6 },
                { wch: 48 }, { wch: 10 }, { wch: 6 }, { wch: 16 }, { wch: 16 }, { wch: 44 },
            ];
            worksheet["!rows"] = [{ hpt: 26 }, { hpt: 20 }, { hpt: 32 }, { hpt: 32 }];
            return worksheet;
        };

        const workbook = XLSX.utils.book_new();
        if (recordsExibidos.length > 0) {
            XLSX.utils.book_append_sheet(workbook, montarPlanilhaNotas(), "Materiais por Obra");
        }
        if (itensPedido.length > 0) {
            XLSX.utils.book_append_sheet(workbook, montarPlanilhaPedidos(), "Pedidos de Compra");
        }
        const nomeObra = obraSelecionada
            ? `${obraSelecionada.codigoProjeto}_${obraSelecionada.nomeProjeto}`.replace(/[^\w]+/g, "_").slice(0, 60)
            : "OBRA";
        XLSX.writeFile(workbook, `Relatorio_Materiais_Obra_${nomeObra}.xlsx`);
    };

    const valorTotalExibido = useMemo(
        () => recordsExibidos.reduce((acc, r) => acc + (Number(r.valorTotal) || 0), 0),
        [recordsExibidos]
    );

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div>
                    <h1 className="text-stone-800 text-xl md:text-2xl font-medium">Relatório de Materiais por Obra</h1>
                    <p className="text-sm text-stone-600 mt-1">
                        Materiais consumidos na obra, a partir dos itens das notas fiscais vinculadas a ela, apontando em
                        qual fornecedor cada material saiu <b>mais barato</b> e <b>mais caro</b>. A comparação é feita pelo
                        preço médio de cada fornecedor, não por nota isolada. Logo abaixo, em separado, aparecem os itens
                        dos <b>pedidos de compra</b> da obra.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
                    <div className="flex flex-col gap-1.5 md:col-span-3 lg:col-span-2">
                        <label htmlFor="obraId" className="text-slate-700 text-sm font-medium">Obra (obrigatório)</label>
                        <select
                            id="obraId"
                            className={`w-full px-3 py-2 bg-white rounded-md border ${erroObra ? "border-red-500" : "border-slate-300"} disabled:bg-slate-100`}
                            value={filters.obraId}
                            onChange={(e) => { setFilters({ ...filters, obraId: e.target.value }); setErroObra(""); }}
                            disabled={carregandoObras}
                            aria-invalid={erroObra ? "true" : "false"}
                            aria-describedby={erroObra ? "erro-obra" : undefined}
                        >
                            <option value="">{carregandoObras ? "Carregando obras..." : "Selecione a obra"}</option>
                            {obras.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {`${o.codigoProjeto} - ${o.nomeProjeto}`.toUpperCase()}
                                </option>
                            ))}
                        </select>
                        {erroObra && <span id="erro-obra" className="text-red-600 text-xs">{erroObra}</span>}
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="dataInicio" className="text-slate-700 text-sm font-medium">Data Inicial (Emissão)</label>
                        <input
                            id="dataInicio"
                            type="date"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.dataInicio}
                            onChange={(e) => setFilters({ ...filters, dataInicio: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="dataFim" className="text-slate-700 text-sm font-medium">Data Final (Emissão)</label>
                        <input
                            id="dataFim"
                            type="date"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.dataFim}
                            onChange={(e) => setFilters({ ...filters, dataFim: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="chaveComparacao" className="text-slate-700 text-sm font-medium">Comparar por</label>
                        <select
                            id="chaveComparacao"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.chaveComparacao}
                            onChange={(e) => setFilters({ ...filters, chaveComparacao: e.target.value })}
                        >
                            <option value="descricao">Mesma descrição (preciso)</option>
                            <option value="ncm">Mesmo NCM e unidade (amplo)</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="escopoComparacao" className="text-slate-700 text-sm font-medium">Histórico de preço</label>
                        <select
                            id="escopoComparacao"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.escopoComparacao}
                            onChange={(e) => setFilters({ ...filters, escopoComparacao: e.target.value })}
                        >
                            <option value="global">Todas as obras</option>
                            <option value="obra">Somente esta obra</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                        <label htmlFor="busca" className="text-slate-700 text-sm font-medium">Material (contém)</label>
                        <input
                            id="busca"
                            type="text"
                            placeholder="Ex.: cimento"
                            className="w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            value={filters.busca}
                            onChange={(e) => setFilters({ ...filters, busca: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter" && !isLoading) fetchRelatorio(); }}
                        />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                    <button
                        type="button"
                        className="px-4 py-2 bg-white rounded-md border border-stone-500 disabled:opacity-60"
                        onClick={clearFilters}
                        disabled={isLoading}
                    >
                        <span className="text-stone-500 text-sm">Limpar</span>
                    </button>
                    <button
                        type="button"
                        className="px-4 py-2 bg-stone-600 rounded-md disabled:opacity-60"
                        onClick={fetchRelatorio}
                        disabled={isLoading}
                    >
                        <span className="text-white text-sm">{isLoading ? "Filtrando..." : "Filtrar"}</span>
                    </button>
                    <button
                        type="button"
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md flex items-center gap-2 disabled:opacity-60"
                        onClick={exportarExcel}
                        disabled={isLoading || (recordsExibidos.length === 0 && itensPedido.length === 0)}
                        title={recordsExibidos.length === 0 && itensPedido.length === 0 ? "Filtre os dados para habilitar a exportação" : "Exportar para Excel"}
                    >
                        <img className="w-4 h-4" src="/images/icon/upload.png" alt="" />
                        <span>Exportar Excel</span>
                    </button>
                    {records.length > 0 && (
                        <label className="flex items-center gap-2 text-sm text-slate-700 ml-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="w-4 h-4"
                                checked={somenteComparaveis}
                                onChange={(e) => setSomenteComparaveis(e.target.checked)}
                            />
                            Mostrar somente materiais com comparação de fornecedor
                        </label>
                    )}
                </div>

                {isLoading && filters.chaveComparacao === "ncm" && (
                    <p className="text-xs text-slate-500 -mt-3">
                        A comparação por NCM analisa todo o histórico de compras e pode levar alguns segundos.
                    </p>
                )}

                {comparacaoPorNcm && !isLoading && records.length > 0 && (
                    <div className="bg-amber-50 border border-amber-300 text-amber-900 text-sm rounded-md px-4 py-3">
                        <b>Comparação ampliada (indicativa):</b> o NCM agrupa produtos <b>parecidos</b>, não idênticos —
                        dois itens de mesmo NCM e mesma unidade podem ser modelos ou bitolas diferentes. Use o resultado
                        como pista de negociação e confira a nota de referência antes de concluir. Por isso a economia
                        potencial não é totalizada neste modo.
                    </div>
                )}

                {!isLoading && hasSearched && records.length > 0 && totais.materiaisSemComparacao > 0 && (
                    <div className="bg-blue-50 border border-blue-300 text-blue-900 text-sm rounded-md px-4 py-3">
                        <b>Cobertura da comparação:</b> {totais.materiaisComparaveis} de {totais.materiais} materiais têm
                        dois ou mais fornecedores para comparar; {totais.materiaisSemComparacao} foram comprados de um
                        único fornecedor{filtrosAplicados?.chaveComparacao === "descricao" ? " com essa mesma descrição" : ""}.
                        {filtrosAplicados?.chaveComparacao === "descricao" && (
                            <> Cada fornecedor descreve o produto do seu jeito na NF-e, então o mesmo item vira materiais
                                distintos — para ampliar a comparação, troque <i>Comparar por</i> para <i>Mesmo NCM e unidade</i>.</>
                        )}
                    </div>
                )}

                {!isLoading && obraSelecionada && records.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white border border-slate-200 rounded-md p-4">
                            <div className="text-xs text-slate-500">Materiais na obra</div>
                            <div className="text-lg font-semibold text-slate-800">{recordsExibidos.length}</div>
                            <div className="text-xs text-slate-500 mt-1">{totais.fornecedoresDistintos} fornecedor(es) na obra</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-md p-4">
                            <div className="text-xs text-slate-500">Valor em materiais</div>
                            <div className="text-lg font-semibold text-slate-800">{formatCurrencyFromDb(valorTotalExibido)}</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-md p-4">
                            <div className="text-xs text-slate-500">Com comparação de fornecedor</div>
                            <div className="text-lg font-semibold text-slate-800">{totais.materiaisComparaveis}</div>
                            <div className="text-xs text-slate-500 mt-1">{totais.materiaisSemComparacao} sem comparação</div>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-300 rounded-md p-4">
                            <div className="text-xs text-emerald-800">Economia potencial</div>
                            <div className="text-lg font-semibold text-emerald-900">
                                {comparacaoPorNcm ? "—" : formatCurrencyFromDb(totais.economiaPotencial)}
                            </div>
                            <div className="text-xs text-emerald-800 mt-1">
                                {comparacaoPorNcm
                                    ? "Não totalizada na comparação por NCM"
                                    : "Comprando pelo preço do fornecedor mais barato"}
                            </div>
                        </div>
                    </div>
                )}

                {isLoading && (
                    <div className="flex justify-center items-center py-8" role="status" aria-live="polite">
                        <div className="text-stone-600 text-sm">Carregando...</div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-left">Material</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Qtd</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Valor na Obra</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Preço Médio</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-left">Menor Preço (fornecedor)</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-left">Maior Preço (fornecedor)</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Variação</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Economia Potencial</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!isLoading && recordsExibidos.length > 0 ? (
                                recordsExibidos.map((r) => (
                                    <React.Fragment key={r.catalogoId}>
                                        <tr className="hover:bg-gray-50 align-top">
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm">
                                                <button
                                                    type="button"
                                                    className="text-left text-slate-800 hover:underline"
                                                    onClick={() => setDetalheAberto(detalheAberto === r.catalogoId ? null : r.catalogoId)}
                                                    aria-expanded={detalheAberto === r.catalogoId}
                                                    title="Ver fornecedores desse material na obra"
                                                >
                                                    {(r.descricao || "").toUpperCase()}
                                                </button>
                                                <div className="text-xs text-slate-500 mt-0.5">
                                                    NCM {r.ncm || "—"} · {r.numNotas} nota(s) · {r.numFornecedoresObra} fornecedor(es) na obra
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right whitespace-nowrap">
                                                {formatarQuantidade(r.quantidadeTotal)} {r.unidadeMedida}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">{formatCurrencyFromDb(r.valorTotal)}</td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">{formatCurrencyFromDb(r.precoMedioObra)}</td>
                                            <td className="px-4 py-2 border border-slate-200 text-sm">
                                                {r.comparavel && r.menorPreco ? (
                                                    <>
                                                        <div className="text-emerald-800 font-medium">{formatCurrencyFromDb(r.menorPreco.valorUnitario)}</div>
                                                        <div className="text-slate-700">{(r.menorPreco.fornecedor || "").toUpperCase()}</div>
                                                        <div className="text-xs text-slate-500">
                                                            {r.menorPreco.numeroNota ? `NF ${r.menorPreco.numeroNota}` : "—"}
                                                            {r.menorPreco.dataEmissao ? ` · ${formatDateWithoutHour(r.menorPreco.dataEmissao)}` : ""}
                                                            {r.menorPreco.obra ? ` · ${r.menorPreco.obra}` : ""}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="text-slate-400">Sem outro fornecedor para comparar</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 text-sm">
                                                {r.comparavel && r.maiorPreco ? (
                                                    <>
                                                        <div className="text-red-800 font-medium">{formatCurrencyFromDb(r.maiorPreco.valorUnitario)}</div>
                                                        <div className="text-slate-700">{(r.maiorPreco.fornecedor || "").toUpperCase()}</div>
                                                        <div className="text-xs text-slate-500">
                                                            {r.maiorPreco.numeroNota ? `NF ${r.maiorPreco.numeroNota}` : "—"}
                                                            {r.maiorPreco.dataEmissao ? ` · ${formatDateWithoutHour(r.maiorPreco.dataEmissao)}` : ""}
                                                            {r.maiorPreco.obra ? ` · ${r.maiorPreco.obra}` : ""}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-sm text-right">
                                                {r.comparavel && r.variacaoPercentual != null
                                                    ? `${r.variacaoPercentual.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 text-sm text-right font-medium text-emerald-800">
                                                {r.comparavel && r.economiaPotencial > 0 ? formatCurrencyFromDb(r.economiaPotencial) : "—"}
                                            </td>
                                        </tr>
                                        {detalheAberto === r.catalogoId && (
                                            <tr className="bg-slate-50">
                                                <td className="px-4 py-3 border border-slate-200 text-sm text-slate-700" colSpan={8}>
                                                    <div className="font-medium mb-1">Fornecedores desse material nesta obra</div>
                                                    <ul className="list-disc pl-5 space-y-0.5">
                                                        {r.fornecedoresObra.map((f, i) => (
                                                            <li key={`${r.catalogoId}-${f.fornecedorId ?? i}`}>
                                                                {(f.fornecedor || "").toUpperCase()} — {formatarQuantidade(f.quantidade)} {r.unidadeMedida}
                                                                {" · "}{formatCurrencyFromDb(f.valorTotal)}
                                                                {f.precoMedio != null && <> · preço médio {formatCurrencyFromDb(f.precoMedio)}</>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <div className="text-xs text-slate-500 mt-2">
                                                        Comparação feita com {r.fornecedoresComparados} fornecedor(es) e {r.observacoes} compra(s) no histórico selecionado.
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : !isLoading ? (
                                <tr>
                                    <td colSpan={8} className="text-center py-10 text-slate-500">
                                        {!hasSearched
                                            ? "Selecione a obra e clique em Filtrar para carregar os materiais."
                                            : records.length > 0
                                                ? "Nenhum material com comparação de fornecedor. Desmarque a opção acima para ver todos."
                                                : "Nenhum material encontrado nas notas fiscais desta obra no período informado."}
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                        {!isLoading && recordsExibidos.length > 0 && (
                            <tfoot>
                                <tr className="bg-stone-100 font-semibold">
                                    <td className="px-4 py-2 border border-slate-200 text-right" colSpan={2}>Total</td>
                                    <td className="px-4 py-2 border border-slate-200 text-right">{formatCurrencyFromDb(valorTotalExibido)}</td>
                                    <td className="px-4 py-2 border border-slate-200" colSpan={4}></td>
                                    <td className="px-4 py-2 border border-slate-200 text-right text-emerald-800">
                                        {comparacaoPorNcm ? "—" : formatCurrencyFromDb(totais.economiaPotencial)}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                {!isLoading && hasSearched && pedidosCompra.permitido && (
                    <section className="flex flex-col gap-3" aria-labelledby="titulo-pedidos-compra">
                        <div>
                            <h2 id="titulo-pedidos-compra" className="text-stone-800 text-lg font-medium">
                                Itens de Pedidos de Compra da obra
                            </h2>
                            <p className="text-sm text-stone-600 mt-1">
                                Pedidos aprovados, em andamento, recebidos ou concluídos, pela data de emissão do pedido.
                            </p>
                        </div>

                        <div className="bg-amber-50 border border-amber-300 text-amber-900 text-sm rounded-md px-4 py-3">
                            <b>Estes valores não entram no total das notas fiscais acima.</b> Um pedido pode já estar
                            faturado numa nota da obra — nesse caso a mesma compra aparece nas duas listas. A coluna
                            <i> Nota de referência</i> aponta a nota vinculada ao pedido ou, sem vínculo, uma nota do mesmo
                            fornecedor nesta obra com valor e data compatíveis. Confira antes de somar.
                        </div>

                        {itensPedido.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="bg-white border border-slate-200 rounded-md p-4">
                                    <div className="text-xs text-slate-500">Itens em pedidos</div>
                                    <div className="text-lg font-semibold text-slate-800">{pedidosCompra.totais?.itens || 0}</div>
                                    <div className="text-xs text-slate-500 mt-1">{pedidosCompra.totais?.pedidos || 0} pedido(s)</div>
                                </div>
                                <div className="bg-white border border-slate-200 rounded-md p-4">
                                    <div className="text-xs text-slate-500">Valor dos itens em pedidos</div>
                                    <div className="text-lg font-semibold text-slate-800">{formatCurrencyFromDb(pedidosCompra.totais?.valorTotal || 0)}</div>
                                    <div className="text-xs text-slate-500 mt-1">Soma dos itens, sem desconto e frete do pedido</div>
                                </div>
                                <div className="bg-white border border-slate-200 rounded-md p-4">
                                    <div className="text-xs text-slate-500">Com nota de referência</div>
                                    <div className="text-lg font-semibold text-slate-800">{formatCurrencyFromDb(pedidosCompra.totais?.valorComNotaReferencia || 0)}</div>
                                    <div className="text-xs text-slate-500 mt-1">{pedidosCompra.totais?.pedidosComNotaReferencia || 0} pedido(s) possivelmente já faturado(s)</div>
                                </div>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border-collapse border border-gray-200">
                                <thead className="bg-stone-200">
                                    <tr>
                                        <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-left">Pedido</th>
                                        <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-left">Item</th>
                                        <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Qtd</th>
                                        <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Preço Unitário</th>
                                        <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-right">Valor</th>
                                        <th className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold text-left">Nota de Referência</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itensPedido.length > 0 ? (
                                        itensPedido.map((i) => (
                                            <tr key={i.itemId} className="hover:bg-gray-50 align-top">
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-slate-700 whitespace-nowrap">
                                                    <div className="text-slate-800 font-medium">{i.numeroOC}</div>
                                                    <div className="text-xs text-slate-500">
                                                        {i.dataEmissao ? formatDateWithoutHour(i.dataEmissao) : "—"} · {i.status}
                                                    </div>
                                                    <div className="text-xs text-slate-500 max-w-[16rem] truncate" title={i.fornecedor}>
                                                        {(i.fornecedor || "").toUpperCase()}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-slate-700">
                                                    {(i.descricao || "").toUpperCase()}
                                                </td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-slate-700 text-right whitespace-nowrap">
                                                    {formatarQuantidadePedido(i.quantidade)} {i.unidade}
                                                </td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-slate-700 text-right whitespace-nowrap">
                                                    {formatarPrecoUnitario(i.precoUnitario)}
                                                </td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-slate-700 text-right whitespace-nowrap">
                                                    {formatCurrencyFromDb(i.valorTotal)}
                                                </td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm">
                                                    {i.notaReferencia ? (
                                                        <span className={i.notaReferencia.tipo === "VINCULADA" ? "text-slate-700" : "text-amber-800"}>
                                                            {descreverNotaReferencia(i.notaReferencia)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400">Sem nota identificada</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="text-center py-8 text-slate-500">
                                                Nenhum item de pedido de compra encontrado para esta obra com os filtros informados.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                {itensPedido.length > 0 && (
                                    <tfoot>
                                        <tr className="bg-stone-100 font-semibold">
                                            <td className="px-4 py-2 border border-slate-200 text-right" colSpan={4}>Total dos pedidos</td>
                                            <td className="px-4 py-2 border border-slate-200 text-right whitespace-nowrap">{formatCurrencyFromDb(pedidosCompra.totais?.valorTotal || 0)}</td>
                                            <td className="px-4 py-2 border border-slate-200"></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </section>
                )}

                <AlertDialog isVisible={showAlertDialog} message={message} onClose={() => setShowAlertDialog(false)} />
            </div>
        </Template>
    );
};

export default RelatorioMateriaisObra;
